import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faBookOpen,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Platform02GuideExplorer } from "../Platform02GuideModal.js";
import { getGuidePageByPath } from "../Platform02GuideData.js";
import platformApplicationsImage from "../../assets/img/illustrations/International trade-bro.png";
import platformEventsImage from "../../assets/img/illustrations/Usability testing-bro.png";
import { HELP_CENTER_OPEN_EVENT, openHelpCenter } from "./helpCenterEvents.js";
import { HelpLink, HelpNavigationProvider, useHelpLocation } from "./HelpNavigation.js";

const PLATFORM_APPLICATIONS_PATH = "/ajuda/plataforma-aplicativos";
const PLATFORM_EVENTS_PATH = "/ajuda/plataforma-eventos";

const getHelpLocation = (path) => {
  const url = new URL(path, window.location.origin);
  return { pathname: url.pathname, search: url.search, hash: url.hash };
};

const normalizePath = (pathname) => pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

const getSelectedPlatformTitle = (pathname) => {
  const normalizedPath = normalizePath(pathname);
  if (normalizedPath === PLATFORM_APPLICATIONS_PATH || normalizedPath.startsWith(`${PLATFORM_APPLICATIONS_PATH}/`)) {
    return "Plataforma de aplicativos";
  }
  if (normalizedPath === PLATFORM_EVENTS_PATH || normalizedPath.startsWith(`${PLATFORM_EVENTS_PATH}/`)) {
    return "Plataforma de eventos";
  }
  return null;
};

const HelpStageBackButton = ({ to, state, onBack }) => {
  const content = <>
    <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
    <span>Voltar</span>
  </>;

  if (onBack) {
    return (
      <button type="button" onClick={onBack} className="help-center-stage-back" aria-label="Voltar">
        {content}
      </button>
    );
  }

  return (
    <HelpLink to={to} state={state} className="help-center-stage-back" aria-label="Voltar">
      {content}
    </HelpLink>
  );
};

const HelpPlatformSelector = ({ onClose }) => (
  <div className="help-center-stage help-center-platform-selector">
    <HelpStageBackButton onBack={onClose} />
    <div className="help-center-stage-heading">
      <span>Central de conhecimento</span>
      <h1>Escolha uma plataforma</h1>
    </div>
    <div className="help-center-platform-grid">
      <HelpLink to={PLATFORM_APPLICATIONS_PATH}>
        <span className="help-center-platform-image"><img src={platformApplicationsImage} alt="" /></span>
        <span className="help-center-platform-copy">
          <strong>Plataforma de aplicativos</strong>
          <small>Crie um evento e selecione aplicações para serem utilizadas! Compartilhe o link para qualquer pessoa acessar e participar</small>
        </span>
        <FontAwesomeIcon icon={faArrowRight} />
      </HelpLink>
      <HelpLink to={PLATFORM_EVENTS_PATH}>
        <span className="help-center-platform-image"><img src={platformEventsImage} alt="" /></span>
        <span className="help-center-platform-copy">
          <strong>Plataforma de eventos</strong>
          <small>Aproveite os benefícios de uma aplicação personalizada para divulgação e controle do evento e permita que participantes se conectem com mais facilidade</small>
        </span>
        <FontAwesomeIcon icon={faArrowRight} />
      </HelpLink>
    </div>
  </div>
);

const PlatformApplicationsGuide = () => (
  <div className="help-center-stage help-center-platform-empty">
    <HelpStageBackButton to="/ajuda" />
    <h1>Plataforma de aplicativos</h1>
    <div className="help-center-empty-canvas" aria-hidden="true" />
  </div>
);

const HelpNotFound = ({ platformEvents = false }) => (
  <div className="help-center-stage help-center-not-found">
    <HelpStageBackButton to="/ajuda" />
    <span>404</span>
    <h1>Página de ajuda não encontrada</h1>
    <div>
      {platformEvents && (
        <HelpLink to={PLATFORM_EVENTS_PATH}>Ir para Plataforma de eventos</HelpLink>
      )}
      <HelpLink to="/ajuda">Escolher plataforma</HelpLink>
    </div>
  </div>
);

const HelpPlatformContent = ({ pathname, onClose }) => {
  const normalizedPath = normalizePath(pathname);
  if (normalizedPath === "/ajuda") return <HelpPlatformSelector onClose={onClose} />;
  if (normalizedPath === PLATFORM_APPLICATIONS_PATH) {
    return <PlatformApplicationsGuide />;
  }
  if (normalizedPath.startsWith(`${PLATFORM_APPLICATIONS_PATH}/`)) {
    return <HelpNotFound />;
  }
  if (normalizedPath === PLATFORM_EVENTS_PATH || normalizedPath.startsWith(`${PLATFORM_EVENTS_PATH}/`)) {
    const page = getGuidePageByPath(normalizedPath);
    return page
      ? <Platform02GuideExplorer page={page} />
      : <HelpNotFound platformEvents />;
  }
  return <HelpNotFound />;
};

const HelpCenterModalContent = ({ onClose }) => {
  const location = useHelpLocation();
  const selectedPlatformTitle = getSelectedPlatformTitle(location.pathname);

  return (
    <>
      {selectedPlatformTitle && (
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="platform02-guide-modal-icon"><FontAwesomeIcon icon={faBookOpen} /></span>
            <span><small>Central de conhecimento</small>{selectedPlatformTitle}</span>
          </Modal.Title>
        </Modal.Header>
      )}
      <Modal.Body className="p-0">
        <HelpPlatformContent pathname={location.pathname} onClose={onClose} />
      </Modal.Body>
    </>
  );
};

export const HelpCenter = ({ isAdmin = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHelpPath = location.pathname === "/ajuda" || location.pathname.startsWith("/ajuda/");
  const returnTo = location.state?.returnTo;
  const [isOpen, setIsOpen] = useState(isHelpPath);
  const [helpLocation, setHelpLocation] = useState(() => getHelpLocation(
    isHelpPath ? `${location.pathname}${location.search}${location.hash}` : "/ajuda",
  ));

  const navigateHelp = (to, state) => {
    const nextLocation = getHelpLocation(typeof to === "string" ? to : to?.pathname || "/ajuda");
    setHelpLocation({ ...nextLocation, state });
  };

  useEffect(() => {
    const handleOpenHelp = (event) => {
      const requestedPath = event.detail?.path;
      const nextPath = typeof requestedPath === "string" && requestedPath.startsWith("/ajuda")
        ? requestedPath
        : "/ajuda";
      setHelpLocation(getHelpLocation(nextPath));
      setIsOpen(true);
    };

    window.addEventListener(HELP_CENTER_OPEN_EVENT, handleOpenHelp);
    return () => window.removeEventListener(HELP_CENTER_OPEN_EVENT, handleOpenHelp);
  }, []);

  useEffect(() => {
    if (!isHelpPath) return;
    setHelpLocation(getHelpLocation(`${location.pathname}${location.search}${location.hash}`));
  }, [isHelpPath, location.hash, location.pathname, location.search]);

  const closeHelp = () => {
    setIsOpen(false);
    if (isHelpPath) {
      navigate(typeof returnTo === "string" && !returnTo.startsWith("/ajuda") ? returnTo : "/", { replace: true });
    }
  };

  if (!isAdmin) return null;

  return (
    <>
      <div className="sidebar-help-center">
        <Button type="button" className="sidebar-help-center-button" aria-label="Central de ajuda" title="Central de ajuda" onClick={() => openHelpCenter()}>
          <FontAwesomeIcon icon={faQuestionCircle} />
          <h5 className="mb-0"><strong className="text-black">Central de ajuda</strong></h5>
          <FontAwesomeIcon icon={faArrowRight} />
        </Button>
      </div>

      <Modal
        show={isOpen || isHelpPath}
        onHide={closeHelp}
        fullscreen
        className="platform02-guide-modal help-center-modal"
        backdropClassName="help-center-modal-backdrop"
      >
        {(isOpen || isHelpPath) && (
          <HelpNavigationProvider location={helpLocation} navigate={navigateHelp}>
            <HelpCenterModalContent onClose={closeHelp} />
          </HelpNavigationProvider>
        )}
      </Modal>
    </>
  );
};
